import { openDB, type IDBPDatabase } from 'idb';
import type { UploadFailureStep } from '@arthelper/shared/upload-failure';
import type { UploadSource } from '@arthelper/shared/upload-origin';

/**
 * Uploads that broke before an image row existed (picking, the signed URL, the S3 PUT,
 * createImage). The server never heard of them, so without this a reload erases every
 * trace (#4159). The file itself is kept so Retry works after a reload too.
 *
 * Best effort: private mode, a full disk or a browser without IndexedDB all fall back to
 * the in-memory failure the context already shows for this session.
 */
export interface StoredFailedUpload {
  id: string;
  userId: string;
  file: Blob;
  fileName: string;
  fileType: string;
  step: UploadFailureStep;
  message: string;
  collectionId: string | null;
  /** Absent on rows saved before #4558. */
  uploadSource?: UploadSource;
  failedAt: string;
}

const DB_NAME = 'arthelper-failed-uploads';
const STORE = 'uploads';
/** Newest kept per member; older failures drop off rather than filling the disk. */
const MAX_PER_USER = 20;

let dbPromise: Promise<IDBPDatabase> | null = null;

function db() {
  dbPromise =
    dbPromise ??
    openDB(DB_NAME, 1, {
      upgrade(database) {
        database.createObjectStore(STORE, { keyPath: 'id' });
      },
    });
  return dbPromise;
}

async function all(userId: string): Promise<StoredFailedUpload[]> {
  const rows: StoredFailedUpload[] = await (await db()).getAll(STORE);
  return rows.filter((row) => row.userId === userId).sort((a, b) => b.failedAt.localeCompare(a.failedAt));
}

export async function listFailedUploads(userId: string): Promise<StoredFailedUpload[]> {
  try {
    return await all(userId);
  } catch (error) {
    console.warn('[failed-uploads] read skipped:', error);
    return [];
  }
}

export async function saveFailedUpload(entry: StoredFailedUpload): Promise<void> {
  try {
    const database = await db();
    await database.put(STORE, entry);
    const overflow = (await all(entry.userId)).slice(MAX_PER_USER);
    await Promise.all(overflow.map((row) => database.delete(STORE, row.id)));
  } catch (error) {
    console.warn('[failed-uploads] write skipped:', error);
  }
}

export async function removeFailedUpload(id: string): Promise<void> {
  try {
    await (await db()).delete(STORE, id);
  } catch (error) {
    console.warn('[failed-uploads] delete skipped:', error);
  }
}
